import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { uploadFileToS3 } from '@/helpers';
import { useApiMutation, useApiQuery, useFetch } from '@/modules/common';

import { EventDetailsTab } from '../constants';
import {
  EventDetails,
  EventUploadUrlResponse,
  MediaField,
  SaveMediaInput,
  UpdateEventMediaRequest,
  UpdateEventPayload,
} from '../types';

const tabEndpointMapper = {
  [EventDetailsTab.Info]: 'info',
  [EventDetailsTab.AIConfiguration]: 'ai-config',
  [EventDetailsTab.Models]: 'models',
  [EventDetailsTab.Styles]: 'styles',
  [EventDetailsTab.Masks]: 'masks',
  [EventDetailsTab.Printers]: 'printers',
  [EventDetailsTab.LacesColors]: 'laces-colors',
  [EventDetailsTab.CollarColors]: 'collar-colors',
};

export function useEventDetails(eventId: string, tab: string) {
  const queryClient = useQueryClient();
  const fetchClient = useFetch();

  const eventQuery = useApiQuery<EventDetails>(`/events/${eventId}`, {
    queryKey: ['eventDetails', String(eventId)],
    initialData: null,
  });

  // For a changed media slot, uploads the new file (if any) and resolves to the
  // temp key to send in PATCH /media. Returns `null` when the slot was cleared,
  // and `undefined` when nothing changed (so the key is omitted from the body).
  const resolveMediaKey = async (
    { value, isDirty }: MediaField,
    getUploadUrl: (file: File) => Promise<EventUploadUrlResponse>
  ): Promise<string | null | undefined> => {
    if (!isDirty) return undefined;

    if (value instanceof File) {
      const { url, key } = await getUploadUrl(value);
      await uploadFileToS3({ file: value, uploadUrl: url });
      return key;
    }

    return null;
  };

  const saveMediaMutation = useMutation<
    EventDetails | null,
    undefined,
    SaveMediaInput
  >({
    mutationFn: async ({ video, idleStateVideo, backgroundImage }) => {
      const [videoKey, idleStateVideoKey, backgroundImageKey] =
        await Promise.all([
          resolveMediaKey(video, () =>
            fetchClient(`/events/${eventId}/video-upload-url`, {
              method: 'GET',
            })
          ),
          resolveMediaKey(idleStateVideo, () =>
            fetchClient(`/events/${eventId}/idle-state-video-upload-url`, {
              method: 'GET',
            })
          ),
          resolveMediaKey(backgroundImage, (file) =>
            fetchClient(
              `/events/${eventId}/background-image-upload-url?contentType=${file.type}`,
              { method: 'GET' }
            )
          ),
        ]);

      const body: UpdateEventMediaRequest = {};
      if (videoKey !== undefined) {
        body.videoKey = videoKey;
      }

      if (idleStateVideoKey !== undefined) {
        body.idleStateVideoKey = idleStateVideoKey;
      }

      if (backgroundImageKey !== undefined) {
        body.backgroundImageKey = backgroundImageKey;
      }

      if (Object.keys(body).length === 0) {
        return null;
      }

      return fetchClient(`/events/${eventId}/media`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(['eventDetails', String(eventId)], data);
      }
    },
  });

  const updateEventMutation = useApiMutation<UpdateEventPayload, EventDetails>(
    tabEndpointMapper[tab]
      ? `/events/${eventId}/${tabEndpointMapper[tab]}`
      : undefined,
    {
      method: 'PATCH',
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['eventDetails', String(eventId)], data);
      },
      onError: (error) => {
        toast.error(
          `Uh oh! Something went wrong. ${error?.message?.slice(0, 50)}`
        );
      },
    }
  );

  return {
    eventQuery,
    updateEventMutation,
    saveMediaMutation,
    isUpdateLoading: updateEventMutation.isPending || saveMediaMutation.isPending,
  } as const;
}
